<template>
  <div class="sf-el button-element">
    <a
      :href="url"
      :target="openInNewTab ? '_blank' : '_self'"
      :rel="openInNewTab ? 'noopener noreferrer' : undefined"
      class="button-element__link"
      :class="{
        'button-element__link--disabled': !url || url.trim() === '',
        'button-element__link--full-width': width === 'full-width',
        'button-element__link--inline': width === 'inline',
      }"
      @click="handleClick"
    >
      {{ label }}
    </a>
  </div>
</template>

<script>
  import { defineComponent } from 'vue';

  export default defineComponent({
    name: 'ButtonElement',
    props: {
      label: {
        type: String,
        default: 'Click me',
        description: 'The text displayed on the button',
      },
      url: {
        type: String,
        default: 'https://example.com',
        description: 'The URL the button links to',
      },
      openInNewTab: {
        type: Boolean,
        default: true,
        description: 'Whether to open the link in a new tab',
      },
      width: {
        type: String,
        default: 'inline',
        description: 'Button width style',
        validator: (value) => ['full-width', 'inline'].includes(value),
      },
      // Additional props that FormElement passes
      modelValue: {
        type: [String, Number, Boolean, Array, Object],
        default: null,
      },
      formData: {
        type: Object,
        default: () => ({}),
      },
      form: {
        type: Object,
        default: () => ({}),
      },
      fieldId: {
        type: String,
        default: '',
      },
      context: {
        type: String,
        default: 'form',
      },
    },
    setup(props) {
      const handleClick = (event) => {
        // In editor context, prevent navigation
        if (props.context === 'editor') {
          event.preventDefault();
          return;
        }

        // If no URL or empty URL, prevent navigation
        if (!props.url || props.url.trim() === '') {
          event.preventDefault();
          return;
        }
      };

      return {
        handleClick,
      };
    },
  });
</script>

<style scoped lang="scss">
  .button-element {
    margin: 1rem 0;

    &__link {
      display: inline-block;
      background: var(--cs-button-default-background, #007bff);
      color: var(--cs-button-default-text, #fff);
      text-decoration: none;
      padding: 0.75rem 1.5rem;
      border-radius: 0.375rem;
      font-weight: 500;
      font-size: 1rem;
      transition: all 0.2s ease;
      border: 2px solid var(--cs-button-default-background, #007bff);
      cursor: pointer;
      text-align: center;
      min-width: 120px;

      &:hover:not(&--disabled) {
        background: var(--cs-button-hover-background, #0056b3);
        border-color: var(--cs-button-hover-background, #0056b3);
        transform: translateY(-1px);
        opacity: 0.8;
      }

      &:active:not(&--disabled) {
        transform: translateY(0);
        box-shadow: 0 2px 4px rgba(0, 123, 255, 0.2);
      }

      &--disabled {
        background: var(--cs-input-default-border, #dee2e6);
        border-color: var(--cs-input-default-border, #dee2e6);
        color: var(--cs-text-secondary, #666);
        cursor: not-allowed;
        opacity: 0.6;
      }

      &--full-width {
        display: block;
        width: 100%;
        text-align: center;
      }

      &--inline {
        display: inline-block;
        width: auto;
      }
    }
  }

  // Editor context styles
  .sf-el--context-editor {
    .button-element {
      &__link {
        position: relative;

        &:hover::after {
          opacity: 1;
        }
      }
    }
  }
</style>
